import React, { useState } from 'react';
import SpinningLoader from 'common-components/loading/SpinningLoader';
import _ from 'lodash';
import { BookingType, FilterType, ShiftSlotStatus } from 'utilities/enum-utils';
import SelectWorkerSearchModal from 'views/bookings-v2/other/components/SelectWorkerSearchModal';
import FoundWorkerRow from 'views/bookings-v2/other/components/FoundWorkerRow';
import { FilterSection } from 'common-components/filter';
import { Anchor, Button, Grid, Group, Paper, Stack, Switch, Text, Title } from '@good/ui/core';
import { Search } from '@good/icons';
import { Address } from '@good/schemas';
import { IFilter } from 'interfaces/filter-interfaces';
import { AssignSessionButton } from './assign-session-button';
import { BookingAssignWorkerActiveFilters, Worker } from './types';

const availableFilters = [
  FilterType.QUALIFICATIONS,
  FilterType.RELIGIONS,
  FilterType.GENDER,
  FilterType.SKILLS,
  FilterType.LANGUAGES,
  FilterType.INTEREST,
];

interface AssignWorkerStep2Props {
  checkForConflict: () => void;
  onAssignWorker: (shiftSlotStatus: ShiftSlotStatus) => void;
  onChangeFilter: (filterKey: keyof BookingAssignWorkerActiveFilters, value: boolean) => void;
  onClickAssign: (shiftSlotStatus: ShiftSlotStatus) => void;
  onOpenAddTimesheet: () => void;
  onSelectWorker: (worker: Worker | null) => void;
  activeFilters: BookingAssignWorkerActiveFilters;
  address: Address | undefined;
  bookingId: string | undefined;
  bookingType: BookingType | undefined;
  customerUserIds: string[] | undefined;
  endDateTime: Date | undefined;
  filteredWorkers: Worker[];
  isAssignedTeamMemberConfirmed: boolean | undefined;
  isBookingCancelled?: boolean;
  isEditingBooking: boolean | undefined;
  isLoadingFoundWorkers: boolean;
  isRecurring: boolean | undefined;
  listFilters: IFilter[];
  onChangeListFilter: (filters: IFilter[]) => void;
  selectedWorker: Worker | null;
  serviceId: string;
  startDateTime: Date | undefined;
  stacked?: boolean;
}

export const AssignWorkerStep2 = ({
  activeFilters,
  address,
  bookingId,
  bookingType,
  customerUserIds,
  endDateTime,
  filteredWorkers,
  isAssignedTeamMemberConfirmed,
  isBookingCancelled,
  isEditingBooking,
  isLoadingFoundWorkers,
  isRecurring,
  listFilters,
  onChangeListFilter,
  serviceId,
  startDateTime,
  selectedWorker,
  onAssignWorker,
  onChangeFilter,
  onSelectWorker,
  onOpenAddTimesheet,
  checkForConflict,
  onClickAssign,
  stacked,
}: AssignWorkerStep2Props) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const onOpenSearchModal = () => {
    onSelectWorker(null);
    setIsSearchOpen(true);
  };

  const onCloseSearchModal = () => {
    setIsSearchOpen(false);
  };

  return (
    <>
      <Text c='dimmed' pb='xs'>
        {isAssignedTeamMemberConfirmed && ' Once assigned, the team member will become confirmed.'}
      </Text>
      <SelectWorkerSearchModal
        isOpen={isSearchOpen}
        selectedWorker={selectedWorker}
        bookingId={bookingId}
        startDateTime={startDateTime}
        endDateTime={endDateTime}
        address={address}
        customerUserIds={customerUserIds}
        serviceId={serviceId}
        onCloseSearchModal={onCloseSearchModal}
        selectWorker={onSelectWorker}
        onAssignWorker={onAssignWorker}
        isBookingCancelled={isBookingCancelled}
      />
      <Grid className='width-full' overflow='hidden'>
        <Grid.Col span={stacked ? 12 : 6}>
          <div className=''>
            <Title order={4} className={'mb-x-small'}>
              Filters
            </Title>
            <Text>Show team members who...</Text>
            <Stack pt='md'>
              <Switch
                checked={activeFilters?.isAvailable}
                onChange={(e) => onChangeFilter('isAvailable', e.currentTarget.checked)}
                label='Available to work this booking'
              />
              <Switch
                checked={activeFilters?.isSkillMatched}
                onChange={(e) => onChangeFilter('isSkillMatched', e.currentTarget.checked)}
                label='Skill matched for the customer(s) care needs'
              />
              <Switch
                checked={activeFilters?.previouslyUsed}
                onChange={(e) => onChangeFilter('previouslyUsed', e.currentTarget.checked)}
                label='Worked with the customer(s) before'
              />
              <Switch
                checked={activeFilters?.isCustomerPreferred}
                label='Preferred team members by the customer'
                onChange={(e) => onChangeFilter('isCustomerPreferred', e.currentTarget.checked)}
              />
              <FilterSection
                availableFilters={availableFilters}
                filters={listFilters}
                onChangeFilter={onChangeListFilter}
                displayTimezone={null}
                usePortal={false}
                verticalDisplay={true}
              />
            </Stack>
          </div>
        </Grid.Col>
        <Grid.Col span={stacked ? 12 : 6}>
          <div className=''>
            <div className='flex-col justify-between align-center mb-medium'>
              <Title order={4}>Team members on roster ({filteredWorkers ? filteredWorkers.length : 0})</Title>
              <Anchor onClick={onOpenSearchModal} fw={550}>
                <Group gap='xs'>
                  <Search />
                  <span>Find team member</span>
                </Group>
              </Anchor>
            </div>
            <Paper withBorder>
              <div style={{ minHeight: '300px', height: 'calc(100vh - 600px)', overflow: 'auto' }}>
                {isLoadingFoundWorkers && <SpinningLoader size={100} message={'Loading'} />}
                {_.isEmpty(filteredWorkers) && !isLoadingFoundWorkers && (
                  <div className='mt-x2-large text-align-center' style={{ margin: 'auto' }}>
                    <Text weight='bold'>No Team member found</Text>
                    <br />
                    <Text>Select a filter or find Team member to select.</Text>
                  </div>
                )}
                {filteredWorkers && filteredWorkers.length > 0 && !isLoadingFoundWorkers && (
                  <table className='width-full'>
                    <tbody>
                      {_.map(filteredWorkers, (worker) => {
                        return (
                          <FoundWorkerRow
                            worker={worker}
                            isSelected={selectedWorker && worker.supportWorkerId === selectedWorker.supportWorkerId}
                            isSearch={false}
                            selectWorker={onSelectWorker}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </Paper>
            {bookingType === BookingType.ACTIVITY_RECORD ? (
              <div className='flex-row justify-end mt-large'>
                <Button
                  disabled={!selectedWorker}
                  onClick={
                    !isEditingBooking ? () => onAssignWorker(ShiftSlotStatus.CONFIRMED) : () => onOpenAddTimesheet()
                  }
                  size='large'
                  className='ml-large'
                >
                  Assign Team member
                </Button>
              </div>
            ) : (
              <div className='flex-row justify-end mt-large'>
                {!isEditingBooking && isRecurring ? (
                  <Button
                    disabled={!selectedWorker}
                    onClick={() => checkForConflict()}
                    size='large'
                    className='ml-large'
                  >
                    Assign Team member
                  </Button>
                ) : (
                  <AssignSessionButton
                    selectedWorker={selectedWorker}
                    onAssignWorker={(shiftSlotStatus: ShiftSlotStatus) => onClickAssign(shiftSlotStatus)}
                    isBookingCancelled={isBookingCancelled}
                  />
                )}
              </div>
            )}
          </div>
        </Grid.Col>
      </Grid>
    </>
  );
};
